// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerDataFetcher.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * Report Designer Data Fetcher.
 */

import fetch from 'share/fetch/Izenda.Fetch';
import uiState from 'logic/Izenda.UiState.js';
import CacheUtils from 'share/utils/Izenda.CacheUtils';
import LoadingManager from 'modules/Izenda.LoadingManager';
import { isNil, isArray, isString, compact } from 'lodash/fp';
const ROOT_URL = 'report/';
const FUSION_URL = 'fusion/';

const getRequestForm = (reportKey: string) => {
  return {
    reportKey: {
      key: reportKey
    }    
  };
};

const fetchers = {
  /**
   * Get all categories
   * @param {string} saveAsType
   * @param {string} tenantId
   * @param {boolean} isGlobal
   * @param {boolean} loadVisible
   * @returns {promise}
   */
  loadCategories(saveAsType: string, tenantId: string, isGlobal: boolean, loadVisible: boolean) {
    isNil(tenantId) && (tenantId = '');
    isNil(isGlobal) && (isGlobal = false);
    isNil(loadVisible) && (loadVisible = false);
    return fetch(
      ROOT_URL + 'category/' + saveAsType + '/' + tenantId + '?isGlobal=' + isGlobal + '&loadVisible=' + loadVisible
    ).get();
  },

  /**
   * Get all sub-categories
   * @param {string} categoryId
   * @param {boolean} loadVisible
   * @returns {promise}
   */
  loadSubCategories(categoryId: string, loadVisible: boolean) {
    isNil(loadVisible) && (loadVisible = false);
    return fetch(ROOT_URL + 'subcategory/' + categoryId + '?loadVisible=' + loadVisible).get();
  },

  /**
   * Get query source
   * @param {string} reportId
   * @returns {promise}
   */
  loadQuerySource(reportId: string) {
    const data = {
      tenantId: uiState.getTenantId(),
      reportKey: {
        key: reportId
      }
    };

    return fetch(ROOT_URL + 'loadDataSourceCategory').post(data);
  },

  /**
   * Load query sources
   * @param {string} querySources
   * @param {any} pageInfo
   * @param {string} reportId
   * @returns {any}
   */
  loadQuerySources(querySources: string, pageInfo: any, reportId: string) {
    pageInfo = pageInfo || {};

    const requestData = {
      objectId: reportId,
      querySources: querySources,
      tenantId: uiState.getTenantId(),
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: pageInfo.sortOrders || [
        {
          key: 'name',
          descending: true
        }
      ]
    };

    return fetch(ROOT_URL + 'loadQuerySources').post(requestData);
  },

  /**
   * Save report designer
   * @param {any} data
   * @returns {promise}
   */
  saveReportDesigner(data: any) {
    return fetch(ROOT_URL).post(data);
  },

  /**
   * Save draft report designer
   * @param {any} data
   * @returns {promise}
   */
  saveDaftReportDesigner(data: any) {
    return fetch(ROOT_URL + 'draft').post(data);
  },

  /**
   * Validate report name
   * @param {any} data
   * @returns {promise}
   */
  validateReportName(data: any) {
    return fetch(ROOT_URL + 'validateReportName').post(data);
  },

  /**
   * Validate report id
   * @param {string} reportId
   * @returns {promise}
   */
  validateReportId: CacheUtils.memoize((reportId: string) => {
    return fetch(ROOT_URL + 'isReportValid/' + reportId).get();
  }),

  /**
   * Validate report id
   * @param {string} reportId
   * @returns {promise}
   */
  validateReportIdNoCache: (reportId: string, fromDesigner: bool) => {
    return fetch(ROOT_URL + 'isReportValid/' + reportId + '?fromdesigner=' + fromDesigner).get();
  },

  /**
   * Validate relationships
   * @param {any} data
   * @returns {promise}
   */
  validateRelationships(data: any) {
    return fetch(ROOT_URL + 'validateRelationships').post(data);
  },

  /**
   * Validate cycle relationship
   * @param {any} data
   */
  validateCycleRelationships(data: any) {
    return fetch(ROOT_URL + 'validateCycleRelationships').post(data);
  },

  /**
   * Validate relationships syntax
   * @param {any} data
   * @returns {promise}
   */
  validateRelationshipSyntax(data: any) {
    return fetch(ROOT_URL + 'validateRelationshipSyntax').post(data);
  },

  /**
   * Validate simple mode relationships
   * @param {any} data
   * @returns {promise}
   */
  validateSimpleModeRelationship(data: any) {
    return fetch(ROOT_URL + 'validateSimpleModeRelationship').post(data);
  },

  /**
   * Filter section: load filter source
   * @param {any} data
   */
  loadFilterSource(data: any) {
    if (!data) {
      return Promise.resolve([]);
    }

    if (isString(data)) {
      data = getRequestForm(data);
    } else {
      // Remove report key when data is report definition
      data.reportKey.key = null;
    }

    return fetch(ROOT_URL + 'loadFilterDataSource').post(data);
  },

  /**
   * Filter section: load filter of certain report
   * @param {string} reportKey
   */
  loadFilterOfReport(reportKey: string) {
    if (!reportKey) {
      return Promise.resolve({});
    }
    return fetch(ROOT_URL + 'loadFilter').post(getRequestForm(reportKey));
  },

  /**
   * Get list of operator group and its operators
   */
  loadFilterOperatorGroup: CacheUtils.memoize(
    () => {
      return fetch(ROOT_URL + 'filter/operators').get();
    },
    { persistent: true }
  ),

  /**
   * Get relationship list
   * @param {string} reportId
   * @param {string} querySources
   * @param {any} pageInfo
   * @param {Array<any>} relationshipOrders
   * @param {boolean} selectedDataSourceChanged
   * @returns {promise}
   */
  getRelationships(
    reportId: string,
    querySources: string,
    pageInfo: any,
    relationshipOrders: Array<any>,
    selectedDataSourceChanged: boolean
  ) {
    pageInfo = pageInfo || {};

    const requestData = {
      objectId: reportId,
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: 1000,
      querySources: querySources,
      relationshipOrders: relationshipOrders,
      tenantId: uiState.getTenantId(),
      selectedDataSourceChanged: !!selectedDataSourceChanged
    };

    return fetch(ROOT_URL + 'loadRelationships/').post(requestData);
  },

  /**
   * Get data for middle panel of Fields
   * @param {any} data
   * @returns {promise}
   */
  availableQuerySourceFields(data: any, isLoadDraft: boolean) {
    if (isString(data)) {
      data = getRequestForm(data);
    } else {
      // Remove report key when data is report definition
      data.reportKey.key = null;
    }

    data.loadDraft = isLoadDraft;
    if (data) {
      return fetch(ROOT_URL + 'availableQuerySourceFields').post(data);
    }
    return Promise.resolve([]);
  },

  /**
   * Get report mode
   * @param {string} reportId
   * @returns {promise}
   */
  getReportMode(reportId: string) {
    const id = reportId ? reportId : '';

    return fetch(ROOT_URL + 'reportMode/' + id).get();
  },

  /**
   * Validate type of filters of subreport
   * @param {string} reportId
   * @param {string} masterReportId
   * @param {boolean} inheritFilter
   * @returns {promise}
   */
  validateFilterFieldsTypeSubReport(reportId: string, masterReportId: string, inheritFilter: boolean) {
    const id = reportId ? reportId : '';
    let url = 'validateFilterFieldsType/' + id;
    !!masterReportId && (url += '?masterReportId=' + masterReportId + '&inheritMasterReportFilter=' + inheritFilter);

    return fetch(ROOT_URL + url).get();
  },

  /**
   * Validate logic syntax of filters.
   * @param {string} syntax
   * @param {Array<any>} filterIndexes
   * @param {Array<any>} filterFields
   * @returns {promise}
   */
  validateFilterSyntax(syntax: string, filterIndexes: Array<any>, filterFields: Array<any>) {
    const requestData = {
      Syntax: syntax,
      AvailableFilter: filterIndexes,
      filterFields: filterFields
    };

    return fetch(ROOT_URL + 'validateFilterSyntax/').post(requestData);
  },

  /**
   * Validate type of filter field
   * @param {any} data
   * @returns {promise}
   */
  validateFilterFieldsType(data) {
    return fetch(ROOT_URL + 'validateFilterFieldsType/').post(data);
  },

  /**
   * Cancel all the modifications in report, it will delete temporary reportKey and back to the previous
   * @param {string} reportKey
   * @returns {promise}
   */
  cancelReportChanges(reportKey: string) {
    const data = {
      reportKey: {
        key: reportKey
      }
    };
    return fetch(ROOT_URL + 'cancel').post(data);
  },

  /**
   * Validate Report Designer
   * @param {any} data
   * @returns {promise}
   */
  validate(data) {
    return fetch(ROOT_URL + 'validate/').post(data);
  },

  /**
   * Load list field for compare field dropdown list.
   * @param {string} reportKey
   * @param {string} dataType
   * @returns {promise}
   */
  loadFilterDataSource(reportKey: string, dataType: string) {
    const requestData = {
      reportKey: {
        key: reportKey
      },
      dataType: dataType
    };
    return fetch(ROOT_URL + 'loadFilterComparisonFields/').post(requestData);
  },

  /** Load list time periods
   * @returns {promise}
   */
  loadTimeperiods: CacheUtils.memoize(
    () => {
      return fetch(ROOT_URL + 'filter/timeperiods/').get();
    },
    { persistent: true }
  ),

  /**
   * Load filter field data
   * @param {any} filterDetails
   * @param {boolean} byTree
   *        TRUE  data is grouped by tree
   *        FALSE data is rended by array
   * @returns {array}
   */
  loadFilterFieldData(filterDetails: any, byTree: boolean) {
    const url = byTree ? 'loadFilterFieldDataAsTree' : 'loadFilterFieldData';

    return fetch(ROOT_URL + url).post(filterDetails);
  },

  /**
   * Load partial filter field data
   * @param  {any} filterDetails
   * @return {promise}
   */
  loadPartialFilterFieldData(filterDetails: any) {
    return fetch(ROOT_URL + 'loadPartialFilterFieldData').post(filterDetails);
  },

  /**
   * Load list of data format of filter properties (based on data-type of field)
   * @param {string} dataType
   * @returns {promise}
   */
  loadFilterDataFormat(dataType: string) {
    if (!dataType) {
      return Promise.resolve([]);
    }
    return fetch(ROOT_URL + 'filter/dataFormat/' + dataType).get();
  },

  /**
   * Load report function
   * @param {number} functionMode
   * @param {string} dataFormatType
   * @param {string} tenatId
   * @param {string} querySourceIds
   * @returns {promise}
   */
  loadReportFunction(functionMode: number, dataFormatType: string, tenatId: string, querySourceIds: string) {
    functionMode = functionMode || '0';

    // querySourceIds can be nil or must be an array
    ensure(isNil(querySourceIds) || isArray(querySourceIds), 'querySourceIds must be an array');

    const params = compact(['function', functionMode, dataFormatType, tenatId]);

    if (!dataFormatType) {
      return Promise.resolve([]);
    }

    return fetch(ROOT_URL + params.join('/')).post({
      querySourceIds: querySourceIds
    });
  },

  /**
   * Load list of data format of filter properties (based on data-type of field)
   * @param {string} dataFormatType
   * @returns {promise}
   */
  loadReportFieldDataFormat(dataFormatType: string) {
    if (!dataFormatType) {
      return Promise.resolve([]);
    }

    return fetch(ROOT_URL + ['field/dataFormat', dataFormatType].join('/')).get();
  },

  /**
   * Load list of available reports by tenant and category
   * @param {string} tenantId
   * @returns {promise} List reports
   */
  loadReports(tenantId: string) {
    const id = tenantId || '';
    return fetch(ROOT_URL + 'allReports/' + id).get();
  },

  /**
   * Load Report data
   * @param {string} reportKey
   * @returns {promise} report data
   */
  loadForEdit(reportKey: string, isLoadDraft: boolean = true, needValidateDataSource: boolean = true, increaseView: boolean = false) {  
    if (!reportKey) {
      return Promise.resolve({});
    }

    let requestData = getRequestForm(reportKey);
    requestData.loadDraft = isLoadDraft;
    if (!needValidateDataSource) {
      requestData.validateReportDataSource = needValidateDataSource;
    }
    // TODO : edit later
    if (increaseView) {
      requestData.increaseView = increaseView;
    }

    return fetch(ROOT_URL + 'loadforedit').post(requestData);
  },

  /**
   * Add calculated field and return added one
   * @param data
   * @returns {promise}
   */
  addCalculatedField(data: any) {
    return fetch(ROOT_URL + 'calculatedfield').post(data);
  },

  /**
   * Delete calculated field
   * @param data
   * @returns {promise}
   */
  deleteReportCalculatedField(data: any) {
    return fetch(ROOT_URL + 'deleteReportCalculatedField').post(data);
  },

  /**
   * Check if this calculated field is used in existing report
   * @param {string} fieldId
   * @returns {promise}
   */
  hasReportUseCalculatedField(fieldId: string) {
    return fetch(ROOT_URL + 'hasReportUseCalculatedField/' + fieldId).get();
  },

  /**
   * Get preview data
   * @param {any} data
   * @returns {promise}
   */
  getPreviewData(data: any) {
    return fetch(FUSION_URL + 'loadData').post(data);
  },

  /**
   * Get preview data of report part
   * @param {any} data
   * @returns {promise}
   */
  getConfigReportPartPreviewData(data: any) {
    return fetch(FUSION_URL + 'reportPartPreview').post(data);
  },

  /**
   * Get random preview data by expanded level
   * @param {any} data
   * @returns {promise}
   */
  getRandomPreviewData(data: any) {
    return fetch(FUSION_URL + 'retrieveRandomExpandedLevelData').post(data);
  },

  /**
   * Validate calculated field
   * @param {any} model
   * @returns {promise}
   */
  validateFieldExpression(model: any) {
    return fetch(FUSION_URL + 'validateExpression').post(model);
  },

  /**
   * Validate SubTotal Expression
   * @param  {any} model
   * @return {promise}
   */
  validateSubTotalExpression(model: any) {
    return fetch(FUSION_URL + 'validateSubTotalExpression').post(model);
  },

  /**
   * Validate Grand Total Expression
   * @param  {any} model
   * @return {promise}
   */
  validateGrandTotalExpression(model: any) {
    return fetch(FUSION_URL + 'validateGrandTotalExpression').post(model);
  },

  /**
   * Detect change when save/save draft report
   * @param {any} data
   * @returns {promise}
   */
  detectReportChange(data: any) {
    return fetch(ROOT_URL + 'detectReportChange').post(data);
  },

  /**
   * Preview result of calculated field in DataSource
   * @param {any} data
   * @returns {string} preview result
   */
  previewDSCalculatedField(data: any) {
    return fetch(FUSION_URL + 'previewCalculatedField').post(data);
  },

  /**
   * Preview result of calculated field in Grand Total
   * @param {any} data
   * @returns {promise}
   */
  previewGrandTotal(data: any) {
    return fetch(FUSION_URL + 'previewGrandTotal').post(data);
  },

  /**
   * Preview result of calculated field in Sub Total
   * @param {any} data
   * @returns {promise}
   */
  previewSubTotal(data: any) {
    return fetch(FUSION_URL + 'previewSubTotal').post(data);
  },

  /**
   * Check relationship is used in report or not
   * @param {string} relationshipId
   * @returns {promise}
   */
  hasReportUseRelationship(relationshipId: string) {
    const relationship = isNil(relationshipId) ? '' : relationshipId;
    return fetch(ROOT_URL + 'hasReportUseRelationship/' + relationship).get();
  },

  /**
   * Fetch definition of report part.
   * @param  {string} id
   * @param  {string} reportId
   * @returns {promise}
   */
  loadReportPartDefinition: CacheUtils.memoize(
    (id: string, reportId: string, page: string) => {
      let url = ROOT_URL + 'reportPart/' + id;
      if (reportId) {
        url += '/' + reportId;
      }
      if (!isNil(page)) {
        url += '?page=' + page;
      }
      return fetch(url).get();
    },
    {
      resolver: (...args) => compact(args).join('_')
    }
  ),

  /**
   * Update result
   * @param  {any} data
   * @returns {promise}
   */
  updateResults(data) {
    return fetch(ROOT_URL + 'updateResults/').post(data);
  },

  /**
   * Load access priority
   * @param {string} id
   * @returns {promise}
   */
  accessPriority(id: string) {
    return fetch(ROOT_URL + 'accessPriority/' + id).get();
  },

  /**
   * Load Partial DataSource Category
   * @param {any} data
   * @returns {promise}
   */
  loadPartialDataSourceCategory(data: any) {
    return fetch(ROOT_URL + 'loadPartialDataSourceCategory').post(data);
  },

  /**
   * Load data source field
   * @param {any} data
   */
  loadDataSourceFields(data: any) {
    return fetch(ROOT_URL + 'loadDataSourceFields').post(data);
  },

  /**
   * Load original query source from relationship id
   * @param {any} data
   * @return {promise}
   */
  loadJoinQuerySourceByRelationship: (data: any) => {
    return fetch(ROOT_URL + 'loadJoinQuerySourceByRelationship').post(data);
  },

  /**
   * Load related query sources
   * @param  {data}
   * @return {promise}
   */
  loadRelatedQuerySources(data: any) {
    return fetch(ROOT_URL + 'loadRelatedQuerySources').post(data);
  },

  /**
   * Load Dynamic Data Source Category
   * @param  {any} data
   * @return {promise}
   */
  loadDynamicDataSourceCategory(data: any) {
    return fetch(ROOT_URL + 'loadDynamicDataSourceCategory').post(data);
  }
};

LoadingManager.linkToLoadingProgress(fetchers, {
  saveReportDesigner: {
    allowToCancel: false
  },

  saveDaftReportDesigner: {
    allowToCancel: false
  },

  addCalculatedField: {
    allowToCancel: false
  },
  deleteReportCalculatedField: {
    allowToCancel: false
  }
});

export default fetchers;



// WEBPACK FOOTER //
// ./ui/fetchers/Izenda.ReportDesignerDataFetcher.js